<template>
  <div>
    <b-card>
      <div class="ht-theme-main">
        <b-spinner
          v-if="isLoading"
          label="Loading..."
          class="mb-2"
          style="display: block; margin: auto"
        />
        <hot-table
          v-else
          ref="hotTable"
          :data="items"
          :rowHeaders="true"
          :colHeaders="colHeaders"
          :columns="columns"
          :settings="settings"
        ></hot-table>
      </div>
    </b-card>
  </div>
</template>
<script>
import { BCard, BSpinner } from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";

import { HotTable } from "@handsontable/vue";
import { registerAllModules } from "handsontable/registry";
import "handsontable/styles/handsontable.min.css";
import "handsontable/styles/ht-theme-main.min.css";

// register Handsontable's modules
registerAllModules();

export default {
  components: {
    BCard,
    BSpinner,
    HotTable,
  },
  data() {
    return {
      colHeaders: [
        "ID",
        "Date",
        "Opening Balance",
        "Collection",
        "Expense",
        "Closing Balance",
        "Difference",
        "Net Closing",
        "Reason",
      ],
      columns: [
        { data: "id", readOnly: true },
        {
          data: "date",
          type: "date",
          dateFormat: "YYYY-MM-DD",
          afterChange: async (changes, source) => {
            if (source === "edit" && changes && changes.length > 0) {
              const [row, prop, oldVal, newVal] = changes[0];
              if (newVal) {
                try {
                  const obj = {
                    url:
                      this.$store.state.domain +
                      "DailyCollectionLog/LoadDetails?db=" +
                      this.$store.state.userData.db +
                      "&date=" +
                      newVal +
                      "&cID=" +
                      this.$store.state.userData.cId,
                    token: this.$store.state.userData.token,
                  };
                  const result = await this.get(obj);
                  console.log("API Response for date:", newVal, result);

                  // Check if result has data property
                  const data = result.data || result;

                  if (data && data !== "NotFound") {
                    // Update the current row with the loaded data
                    const currentRow = this.items[row];
                    const updatedRow = {
                      ...currentRow,
                      openingBalance: Number(data.openingBalance) || 0,
                      collection: Number(data.collection) || 0,
                      expense: Number(data.expenses) || 0,
                      closingBalance: Number(data.closingBalance) || 0,
                      difference: Number(data.difference) || 0,
                      netClosing: Number(data.netClosing) || 0,
                      reason: data.reason || "",
                    };
                    console.log("Updated row data:", updatedRow);
                    this.items.splice(row, 1, updatedRow);

                    // Force table refresh
                    this.$nextTick(() => {
                      this.$refs.hotTable.hotInstance.render();
                    });
                  } else {
                    console.log("No data found for date:", newVal);
                  }
                } catch (error) {
                  console.error("Error loading details:", error);
                  this.$bvToast.toast(
                    "Error loading details for selected date",
                    {
                      title: this.$t("Error"),
                      variant: "danger",
                      solid: true,
                    }
                  );
                }
              }
            }
          },
        },
        { data: "openingBalance", type: "numeric", readOnly: true },
        { data: "collection", type: "numeric", readOnly: true },
        {
          data: "expense",
          type: "numeric",
          validator: function (value, callback) {
            if (value === null || value === undefined || value === "") {
              callback(true);
              return;
            }
            const numValue = Number(value);
            if (isNaN(numValue) || numValue < 0) {
              callback(false);
              return;
            }
            callback(true);
          },
        },
        { data: "closingBalance", type: "numeric", readOnly: true },
        {
          data: "difference",
          type: "numeric",
          validator: function (value, callback) {
            if (value === null || value === undefined || value === "") {
              callback(true);
              return;
            }
            const numValue = Number(value);
            if (isNaN(numValue) || numValue < 0) {
              callback(false);
              return;
            }
            callback(true);
          },
        },
        { data: "netClosing", type: "numeric", readOnly: true },
        { data: "reason", type: "text" },
      ],
      items: [],
      lastEditedRowIndex: null,
      settings: {
        licenseKey: "non-commercial-and-evaluation",
        minSpareRows: 1, // Always show one empty row at the bottom
        stretchH: "all", // Stretch columns to fit the container width
        height: 400, // Fixed height to allow dropdown to display more options
        width: "100%", // Full width
        columnSorting: {
          initialConfig: {
            column: 0, // ID column
            sortOrder: "desc",
          },
        },
        beforeChange: function (changes, source) {
          // Prevent editing of rows with existing IDs (id > 0)
          if (source === "edit") {
            for (let i = 0; i < changes.length; i++) {
              const [row, prop, oldValue, newValue] = changes[i];
              const rowData = this.getSourceDataAtRow(row);
              if (rowData && rowData.id > 0 && prop !== "date") {
                changes[i] = null;
              }
            }
            return changes.filter((change) => change !== null);
          }
          return changes;
        },
        afterChange: (changes, source) => {
          console.log("source:", source);
          if (
            (source === "edit" || source === "Autofill.fill") &&
            changes &&
            changes.length > 0
          ) {
            console.log("Changes:", changes);
            changes.forEach(async ([rowIdx, prop, oldVal, newVal]) => {
              // Only normalize the changed row
              const defaultRow = this.defaultRow();
              const currentRow = this.items[rowIdx];
              const normalizedRow = {};
              for (const key in defaultRow) {
                if (
                  currentRow[key] !== null &&
                  currentRow[key] !== undefined &&
                  currentRow[key] !== ""
                ) {
                  normalizedRow[key] = currentRow[key];
                } else {
                  normalizedRow[key] = defaultRow[key];
                }
              }

              this.lastEditedRowIndex = rowIdx;

              // If date was changed, load the details
              if (prop === "date" && newVal) {
                try {
                  console.log("Loading details for date:", newVal);
                  const obj = {
                    url:
                      this.$store.state.domain +
                      "DailyCollectionLog/LoadDetails?db=" +
                      this.$store.state.userData.db +
                      "&date=" +
                      newVal +
                      "&cID=" +
                      this.$store.state.userData.cId,
                    token: this.$store.state.userData.token,
                  };
                  console.log("API URL:", obj.url);
                  const result = await this.get(obj);
                  console.log("API Response:", result);

                  const data = result.data || result;

                  if (data && data !== "NotFound") {
                    // Update the current row with the loaded data
                    normalizedRow.id = Number(data.id) || 0;
                    normalizedRow.accountID = Number(data.accountID) || 0;
                    normalizedRow.openingBalance =
                      Number(data.openingBalance) || 0;
                    normalizedRow.collection = Number(data.collection) || 0;
                    normalizedRow.expense = Number(data.expenses) || 0;
                    normalizedRow.closingBalance =
                      Number(data.closingBalance) || 0;
                    normalizedRow.difference = Number(data.difference) || 0;
                    normalizedRow.netClosing = Number(data.netClosing) || 0;
                    normalizedRow.reason = data.reason || "";
                    normalizedRow.status = data.status || "Active";

                    console.log(
                      "Normalized row after API update:",
                      normalizedRow
                    );
                  }
                } catch (error) {
                  console.error("Error loading details:", error);
                  this.$bvToast.toast(
                    "Error loading details for selected date",
                    {
                      title: this.$t("Error"),
                      variant: "danger",
                      solid: true,
                    }
                  );
                }
              }

              // Calculate closing balance and net closing
              const opening = Number(normalizedRow.openingBalance) || 0;
              const collection = Number(normalizedRow.collection) || 0;
              const expense = Number(normalizedRow.expense) || 0;
              const difference = Number(normalizedRow.difference) || 0;

              // Calculate closing balance
              normalizedRow.closingBalance = opening + collection - expense;

              // Calculate net closing
              normalizedRow.netClosing =
                normalizedRow.closingBalance - difference;

              // Update the row with normalized data
              this.items.splice(rowIdx, 1, { ...normalizedRow });

              // Get the latest row data after update
              const currentRowData = this.items[rowIdx];
              console.log(
                "Current row data for auto-save check:",
                currentRowData
              );

              // Check if required fields are filled to trigger auto-save
              if (
                currentRowData.date &&
                currentRowData.difference !== null &&
                currentRowData.difference !== undefined &&
                currentRowData.difference !== "" &&
                currentRowData.reason
              ) {
                console.log("Auto-saving row:", currentRowData);
                try {
                  await this.saveCurrentRow(currentRowData);
                  // After successful save, add a new empty row
                  this.items.push(this.defaultRow());
                } catch (error) {
                  console.error("Error auto-saving row:", error);
                }
              }
            });
          }
        },
        afterDocumentKeyDown: (event) => {
          // Save on Ctrl+S
          if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === "s" || event.keyCode === 83)
          ) {
            event.preventDefault();
            // Save the last edited row
            if (this.lastEditedRowIndex !== null) {
              this.saveCurrentRow(this.items[this.lastEditedRowIndex]);
            }
          }
        },
        cells: (row, col) => {
          const cellProperties = {};
          // Make all non-readonly cells readonly for rows where id > 0
          if (this.items && this.items[row] && this.items[row].id > 0) {
            // Only override if the column is not already readonly
            const colDef = this.columns[col];
            if (!colDef.readOnly) {
              cellProperties.readOnly = true;
            }
          }
          return cellProperties;
        },
      },
      accounts: [], // Store accounts data
      defaultRow() {
        return {
          id: 0,
          date: "",
          openingBalance: 0,
          collection: 0,
          expense: 0,
          netClosing: 0,
          difference: 0,
          reason: "",
          closingBalance: 0,
          status: "lock",
        };
      },
      isLoading: false,
    };
  },
  created() {
    this.loadAccounts();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async loadAccounts() {
      this.isLoading = true;
      const obj2 = {
        url:
          this.$store.state.domain +
          "CollectionAccounts?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      let result = await this.get(obj2);
      if (result !== "NotFound") {
        this.accounts = result;
        this.columns[2].source = this.accounts.map((el) => `${el.account}`);
      } else this.accounts = [];
      // Now load daily collection after accounts are loaded
      await this.loadDailyCollection();
      this.isLoading = false;
    },

    async loadDailyCollection() {
      const obj = {
        url:
          this.$store.state.domain +
          "DailyCollectionLog?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      let result = await this.get(obj);
      console.log("result:", result);
      if (result !== "NotFound" && Array.isArray(result)) {
        if (result.length > 0) {
          // Sort the data by ID in descending order
          result.sort((a, b) => Number(b.id) - Number(a.id));

          this.items = result.map((row) => {
            // Format date to YYYY-MM-DD if it's a datetime string
            let date = row.date;
            if (date.includes("T")) {
              date = date.split("T")[0];
            }
            return { ...this.defaultRow(), ...row, date };
          });
          console.log("Sorted items:", this.items);
        }
      }

      // Add an empty row at the end for new entries
      this.items.push(this.defaultRow());
    },

    async LoadAccountSummary(accountID, date) {
      console.log(accountID, date);
      // let obj = {
      //   url:
      //     this.$store.state.domain +
      //     "CollectionAccounts/LoadSummary?db=" +
      //     this.$store.state.userData.db +
      //     "&cID=" +
      //     this.$store.state.userData.cId +
      //     "&accountID=" +
      //     accountID +
      //     "&date=" +
      //     date,
      //   token: this.$store.state.userData.token,
      // };
      // let res = await this.get(obj);
      // return res;
    },

    async saveCurrentRow(row) {
      let saveRow = row;
      if (!saveRow) {
        const rowIndex =
          this.lastEditedRowIndex !== null
            ? this.lastEditedRowIndex
            : this.items.length - 1;
        saveRow = this.items[rowIndex];
      }
      let normalizedRow = { ...this.defaultRow(), ...saveRow };
      console.log("Normalized Row for saving:", normalizedRow);

      // Ensure numeric fields are valid numbers, else set to 0
      [
        "openingBalance",
        "collection",
        "expense",
        "closingBalance",
        "netClosing",
        "difference",
        "accountID",
      ].forEach((field) => {
        if (
          isNaN(Number(normalizedRow[field])) ||
          normalizedRow[field] === null ||
          normalizedRow[field] === ""
        ) {
          normalizedRow[field] = 0;
        } else {
          normalizedRow[field] = Number(normalizedRow[field]);
        }
      });

      // Validate expense and difference are non-negative
      if (normalizedRow.expense < 0) {
        this.$bvToast.toast("Expense cannot be negative", {
          title: "Validation Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (normalizedRow.difference < 0) {
        this.$bvToast.toast("Difference cannot be negative", {
          title: "Validation Error",
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Validate required fields
      const requiredFields = ["date", "difference", "reason", "accountID"];
      const isValid = requiredFields.every(
        (field) =>
          normalizedRow[field] !== "" &&
          normalizedRow[field] !== null &&
          normalizedRow[field] !== undefined
      );

      if (!isValid) {
        this.$bvToast.toast(
          "Please fill all required fields (Date, Difference, Reason, and Account) before saving.",
          {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      // Recalculate closing balance and net closing before saving
      normalizedRow.closingBalance =
        normalizedRow.openingBalance +
        normalizedRow.collection -
        normalizedRow.expense;
      normalizedRow.netClosing =
        normalizedRow.closingBalance - normalizedRow.difference;

      // Ensure status is set
      normalizedRow.status = normalizedRow.status || "Active";
      normalizedRow.accountID = normalizedRow.accountID || 1;

      console.log("Saving row with payload:", normalizedRow);

      // Call the save API
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "DailyCollectionLog?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: normalizedRow,
          context: this,
          message: "Daily collection saved successfully.",
          token: this.$store.state.userData.token,
        };
        const result = await this.post(obj);
        if (result) {
          // Update the current row with the saved data
          const rowIndex = this.items.findIndex((item) => item.id === 0);
          if (rowIndex !== -1) {
            this.items[rowIndex] = { ...normalizedRow, ...result };
          }
          // Show success message
          this.$bvToast.toast("Row saved successfully", {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error saving row:", error);
        this.$bvToast.toast("Error saving row. Please try again.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }
    },
  },
};
</script>

<style>
/* Override Handsontable header styles */

.handsontable .columnSorting.sortAction,
.handsontable .columnSorting:not(.indicatorDisabled).sortAction,
.handsontable .columnSorting {
  max-width: none !important;
  width: auto !important;
  text-overflow: clip !important;
  overflow: visible !important;
}
</style>
